import { signal, computed, type Signal } from '@lit-labs/preact-signals'
import type { TemplateResult } from 'lit'
import {
	AbsenceType,
	Authorisation,
	Colors,
	CustomField,
	EntityType,
	ExpenseType,
	Organisation,
	Person,
	Role,
	Schedule,
	ScreenSettings,
	Tag,
	TimeSettings,
	Project,
	Languages,
	EntityListItem,
	Localisation,
	JournalSettings,
	AttributeType,
	AttributeName,
} from './types'
import { SubscriptionInterval, SubscriptionProduct } from '@shared/subscription'
import { Task } from '@components/task/types'

export type Lang = keyof typeof Languages

export type CurrentEntitySignal = {
	type?: EntityType
	id: string
	parentId?: string
}

export type OrganisationSignal = {
	current: Organisation
}

export type EntitySignal<T> = {
	items?: T[]
	current?: T
	allItems?: T[]
	filteredItems?: T[]
	countArchived?: number
}

export type PersonSignal = EntitySignal<Person>
export type TagSignal = EntitySignal<Tag>
export type ProjectSignal = EntitySignal<Project>
export type TaskSignal = EntitySignal<Task>
export type CustomFieldSignal = EntitySignal<CustomField>
export type AbsenceTypeSignal = EntitySignal<AbsenceType>
export type ExpenseTypeSignal = EntitySignal<ExpenseType>

export type ScheduleSignal = EntitySignal<Schedule>
export type RoleSignal = EntitySignal<Role> & {
	globalAuthorisations?: Authorisation[]
}

export interface IModalAction {
	label: string
	handler: () => void
}

export type ModalSignal = {
	message?: string
	actions?: IModalAction[]
}

/* Entities */
export const organisation = signal<OrganisationSignal | null>(null)
export const person = signal<PersonSignal | null>(null)
export const role = signal<RoleSignal | null>(null)
export const project = signal<ProjectSignal | null>(null)
export const task = signal<TaskSignal | null>(null)
export const tag = signal<TagSignal | null>(null)
export const scheduleType = signal<ScheduleSignal | null>(null)
export const absenceType = signal<AbsenceTypeSignal | null>(null)
export const expenseType = signal<ExpenseTypeSignal | null>(null)
export const customField = signal<CustomFieldSignal | null>(null)

/* UI */
export const aiEnabled = signal(false)
export const attributeClipboard = signal<{
	sourceId: string
	sourceType: EntityType
	attributeTypes: AttributeType[]
	pastedTargetIds: string[]
} | null>(null)
export const isMobile = signal<boolean | undefined>(undefined)
/** Built-in mobile pages (budget-status / planned-vs-real / absence-quota) can
 *  publish a contextual chip here for the parent `bb-mobile-header` to render
 *  in its trailing slot — gives them the same "settings chip" pattern that
 *  custom folders get on the reports list, without leaking layout knowledge. */
export const mobileHeaderTrailing = signal<TemplateResult | undefined>(undefined)
export const colors = signal<Colors | undefined>(undefined)
export const bulkEntitySelected = signal<EntityListItem[]>([])

export const focussedEntityId = signal<string | undefined>(undefined)
export const activeEntityList = signal<HTMLElement | null>(null)

export const screenSettings = signal<ScreenSettings | null>(null)
// The panel edge while it is being dragged, null otherwise so the saved setting shows through
export const draggedPanelRatio = signal<number | null>(null)
// Where the details panel's left edge sits, in % of the viewport width
export const detailsPanelRatio = computed(
	() => draggedPanelRatio.value ?? (screenSettings.value?.detailsPanelRatio || 60)
)

const entitySignals: Record<string, Signal<EntitySignal<{ id: string; name: string }> | null>> = {
	person,
	project,
	task,
	tag,
	absenceType,
}

export function getEntityName(entityType: string, entityId: string): string | undefined {
	if (entityType === 'organisation') {
		const current = organisation.value?.current
		return current && String(current.id) === entityId ? current.name : undefined
	}
	const sig = entitySignals[entityType]?.value
	const item = (sig?.allItems ?? sig?.items)?.find((item) => item.id === entityId) as
		| { name: string; ancestors?: { name: string }[] }
		| undefined
	if (!item) return undefined
	// Hierarchical entities (project, task, tag) carry their root→parent chain in
	// `ancestors` — surface the full path so e.g. "child" reads "Parent: child".
	const path = (item.ancestors ?? []).map((a) => a.name).join(': ')
	return path ? `${path}: ${item.name}` : item.name
}

// Computed UI color: personal color > org color > default (0)
export const uiColor = computed(() => screenSettings.value?.color ?? organisation.value?.current?.color ?? 0)
export const connectedPerson = signal<Person | null>(null)
export const currentEntity = signal<CurrentEntitySignal | null>(null)
export const lang = signal<Lang>('en')
export const modalSignal = signal<ModalSignal>({})
export const timeSettings = signal<TimeSettings | null>(null)
export const localisation = signal<Localisation | null>(null)
export const journalSettings = signal<JournalSettings | null>(null)

// UI state for hiding Intercom launcher (e.g., on mobile timesheet)
export const hideIntercomLauncher = signal(false)

// Set when the startup WebSocket can't be established (e.g. a firewall/proxy blocking wss).
// The app root shows an in-page notice while still at connected===0; cleared on a successful open.
export const connectionTrouble = signal(false)

// True while the app runs over the HTTP fallback transport (the network blocks WebSockets).
// Drives the discrete "degraded connection" indicator in the sidebar.
export const httpFallbackMode = signal(false)

// When a menu entry tries to open a feature-gated page the org doesn't have,
// we surface a feature-preview instead of navigating into the gated page. The
// preview only renders inside <settings-page>, but the trigger can come from
// anywhere (e.g. the global connected-person-sheet, which is a sibling of
// settings-page — a bubbling DOM event would never reach the listener). The
// signal carries the request across the tree; <settings-page> consumes it on
// mount and clears it so navigating away and back doesn't replay the preview.
export const featurePreviewKey = signal<AttributeName | EntityType | null>(null)

// Subscription pricing form state. Held in a signal so the user's interval /
// seat-count / highlight choices survive an in-app nav-away, and snapshotted to
// sessionStorage when a Stripe checkout is initiated so they also survive the
// full-page round-trip out to checkout and back (the cancel_url returns to
// /settings/account/subscription as a fresh page load).
export type SubscriptionFormState = {
	interval: SubscriptionInterval
	seatCount: number
	highlight: SubscriptionProduct
}
export const defaultSubscriptionForm: SubscriptionFormState = {
	interval: SubscriptionInterval.year,
	seatCount: 0,
	highlight: SubscriptionProduct.advanced,
}

const SUBSCRIPTION_FORM_KEY = 'subscriptionForm'

function loadSubscriptionForm(): SubscriptionFormState {
	try {
		const raw = sessionStorage.getItem(SUBSCRIPTION_FORM_KEY)
		if (raw) return { ...defaultSubscriptionForm, ...JSON.parse(raw) }
	} catch {
		// malformed/unavailable storage — fall back to defaults
	}
	return { ...defaultSubscriptionForm }
}

export const subscriptionForm = signal<SubscriptionFormState>(loadSubscriptionForm())

/** Snapshot the current form so it survives the Stripe checkout round-trip. */
export function persistSubscriptionForm() {
	try {
		sessionStorage.setItem(SUBSCRIPTION_FORM_KEY, JSON.stringify(subscriptionForm.value))
	} catch {
		// storage unavailable (private mode / quota) — persistence is best-effort
	}
}

/** Reset the form and drop any persisted snapshot (called at session boundaries). */
export function resetSubscriptionForm() {
	subscriptionForm.value = { ...defaultSubscriptionForm }
	try {
		sessionStorage.removeItem(SUBSCRIPTION_FORM_KEY)
	} catch {
		// ignore
	}
}

/* Timer */
export interface ActiveTimer {
	recordId: string
	startTime: number
	baseDuration: number
	dayStart: number
}

/**
 * One line of the floating shelf: a record the person put there, being timed or parked.
 *
 * Deliberately holds no display data — no name, no colour, not even the activity. All of
 * it is resolved from the record at render time, and an entry whose record is gone is
 * dropped by the reconciliation. Caching a label here is what made the old "resume"
 * snapshot go stale behind the pill.
 */
export interface ShelfEntry {
	recordId: string
	dayStart: number
	/** null while parked — the line stays on the shelf, its play button restarts it. */
	running: { startTime: number; baseDuration: number } | null
}

/**
 * The floating shelf: the activities of the day the person is juggling. A plain click on
 * ANY play affordance — the shelf's own included — keeps its historical meaning (stop the
 * rest, start this) but the stopped lines STAY here, parked: the shelf is a working set
 * and no plain click ever empties it. Ctrl/cmd+click is the one way to ADD a line without
 * stopping anything, identical on every surface.
 *
 * Array order IS display order, and a line holds its slot for its whole life — the shelf is
 * never re-sorted, not even to float the running lines to the top. Position carries no
 * information a row does not already show through its dot, its chrono colour and its button,
 * and moving rows around would shift the next one under a cursor that is about to click.
 */
export const timerShelf = signal<ShelfEntry[]>([])

/**
 * The running timers, derived from the shelf. Everything that only asks "is this record
 * being timed" reads this and never needs to know the shelf exists — row pips, totals,
 * calendar geometry and the duration cells all keep working unchanged.
 */
export const activeTimers = computed<ActiveTimer[]>(() =>
	timerShelf.value
		.filter((entry) => entry.running)
		.map((entry) => ({
			recordId: entry.recordId,
			startTime: entry.running!.startTime,
			baseDuration: entry.running!.baseDuration,
			dayStart: entry.dayStart,
		}))
)

/** The clock every live duration derives from — one interval in timer-state ticks it each
 *  second while a timer runs, and 0 when none does. Nothing stores an elapsed value: a
 *  duration is always `base + (timerNow - startTime)`, computed at read time.
 *
 *  Read this ONLY for a per-second chrono display. Totals and geometry must read
 *  `timerMinuteTick` instead — a computed signal propagates only when its value changes, so
 *  the minute tick fires once a minute from this per-second source and the grid keeps its
 *  current re-render cadence. */
export const timerNow = signal(0)
export const timerMinuteTick = computed(() => Math.floor(timerNow.value / 60000))

export const isAnyTimerActive = computed(() => activeTimers.value.length > 0)

/** The timer running on this record, or undefined. The ONLY way to ask "is this row
 *  running?" — never compare against a single "the timer" id, there isn't one. */
export function timerFor(recordId: string | null | undefined): ActiveTimer | undefined {
	if (!recordId) return undefined
	return activeTimers.value.find((t) => t.recordId === recordId)
}

export function isTimerRunningFor(recordId: string | null | undefined): boolean {
	return !!timerFor(recordId)
}

/**
 * A record's duration with the running timer substituted — the single source of the
 * "ticking total" the whole app shows. Subscribes to the MINUTE tick, so a total calling
 * this re-renders once a minute while still returning an exact value. Every total,
 * section, day and calendar geometry goes through here; nothing recomputes
 * `base + (now - startTime)` on its own.
 */
export function liveDuration(record: { id: string; duration?: number } | null | undefined): number {
	if (!record) return 0
	void timerMinuteTick.value
	const timer = timerFor(record.id)
	// Elapsed clamped to 0: an adopted record can carry a wall-clock start slightly in the
	// future (another device's clock ahead, an API write), and a negative chrono counting
	// up to zero reads as broken.
	if (timer) return timer.baseDuration + Math.max(0, Date.now() - timer.startTime)
	return record.duration ?? 0
}

/**
 * Exact elapsed time of the timer running on `recordId`, or null when none is. Subscribes
 * to the per-SECOND tick — chrono displays only (floating pill, the duration cell in its
 * running state, the mobile timer bar). Totals must use liveDuration instead, or the whole
 * grid re-renders every second.
 */
export function runningElapsed(recordId: string | null | undefined): number | null {
	const timer = timerFor(recordId)
	if (!timer) return null
	void timerNow.value
	// Same clamp as liveDuration: a future startTime must not show a negative chrono.
	return timer.baseDuration + Math.max(0, Date.now() - timer.startTime)
}

// The "resume the timer you just stopped" snapshot used to live here as a single cached
// object. The shelf replaces it: a stopped timer simply stays on the shelf as a parked
// line whose play button restarts it. One less piece of duplicated display state to keep
// in sync — which is what used to go stale.
